import * as React from 'react';
import Box from '@mui/joy/Box';

interface ResponsiveTableWrapperProps {
    children: React.ReactNode;
    minWidth?: number | string;
}

/**
 * Wrapper component for tables that adds horizontal scrolling on mobile devices.
 * Provides smooth touch scrolling and a subtle scrollbar.
 */
export default function ResponsiveTableWrapper({ 
    children, 
    minWidth = 800 
}: ResponsiveTableWrapperProps) {
    return (
        <Box
            sx={{
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                // Scrollbar styling
                '&::-webkit-scrollbar': {
                    height: 8,
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'background.level1',
                    borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'neutral.400',
                    borderRadius: 4,
                    '&:hover': {
                        backgroundColor: 'neutral.500',
                    },
                },
                // Ensure table inside takes minimum width
                '& > table, & > .MuiTable-root, & > .MuiSheet-root > table': {
                    minWidth: minWidth,
                },
            }}
        >
            {children}
        </Box>
    );
}

interface ResponsiveFilterBarProps {
    children: React.ReactNode;
}

/**
 * Wrapper for table filter/search bars that stacks vertically on mobile.
 */
export function ResponsiveFilterBar({ children }: ResponsiveFilterBarProps) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                mb: 2,
                width: '100%',
            }}
        >
            {children}
        </Box>
    );
}

/**
 * Left section of filter bar (typically search + filters)
 */
export function FilterBarLeft({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 },
                flex: 1,
                alignItems: { xs: 'stretch', sm: 'center' },
            }}
        >
            {children}
        </Box>
    );
}

/**
 * Right section of filter bar (typically action buttons)
 */
export function FilterBarRight({ children }: { children: React.ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1,
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                '& > button': {
                    flex: { xs: 1, sm: 'none' },
                },
            }}
        >
            {children}
        </Box>
    );
}
